import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchProducts } from '../api/products';
import { getErrorMessage } from '../api/client';
import { fetchWithRetry } from '../utils/fetchWithRetry';

/** How long cached rows remain acceptable for instant first paint */
const CACHE_FIRST_PAINT_MAX_AGE_MS = 1000 * 60 * 60 * 72; // 72h
/** After failed refresh, reuse cache if younger than this */
const CACHE_FALLBACK_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 14; // 14d
const FOCUS_REFRESH_MIN_AGE_MS = 1000 * 60 * 2; // 2m
const MAX_ITEMS = 500;
const SLOW_LOAD_NOTICE_MS = 1000 * 8;

function cacheKeys(cacheScope) {
  const s = cacheScope && String(cacheScope).trim() ? String(cacheScope).trim() : 'all';
  return {
    session: `iconnectstore_products_cache_v2_${s}`,
    local: `iconnectstore_products_cache_v2_local_${s}`
  };
}

function buildCacheScope({ storeId, limit, fields }) {
  return [
    storeId || 'all',
    limit ? `limit_${limit}` : '',
    fields ? `fields_${fields}` : ''
  ].filter(Boolean).join('_');
}

function parseCache(raw) {
  if (!raw) return null;
  try {
    const { ts, items } = JSON.parse(raw);
    if (!Array.isArray(items) || typeof ts !== 'number') return null;
    return { items, ts };
  } catch {
    return null;
  }
}

function readCachedProducts(storeId) {
  const { session, local } = cacheKeys(storeId);
  let best = null;
  try {
    const sources = [sessionStorage.getItem(session), localStorage.getItem(local)];
    for (const raw of sources) {
      const c = parseCache(raw);
      if (c && (!best || c.ts > best.ts)) best = c;
    }
  } catch {
    /* private mode / disabled storage */
  }
  return best;
}

function isFreshForFirstPaint(cached) {
  if (!cached?.items?.length) return false;
  return Date.now() - cached.ts < CACHE_FIRST_PAINT_MAX_AGE_MS;
}

function isFreshForFallback(cached) {
  if (!cached?.items?.length) return false;
  return Date.now() - cached.ts < CACHE_FALLBACK_MAX_AGE_MS;
}

function writeCachedProducts(storeId, items) {
  const { session, local } = cacheKeys(storeId);
  const payload = JSON.stringify({ ts: Date.now(), items: items.slice(0, MAX_ITEMS) });
  try {
    sessionStorage.setItem(session, payload);
  } catch {
    /* */
  }
  try {
    localStorage.setItem(local, payload);
  } catch {
    /* quota */
  }
}

function clearCachedProducts(storeId) {
  const { session, local } = cacheKeys(storeId);
  try {
    sessionStorage.removeItem(session);
  } catch {
    /* */
  }
  try {
    localStorage.removeItem(local);
  } catch {
    /* */
  }
}

function productLoadErrorMessage(err, fallback) {
  const status = err?.response?.status;
  const code = err?.code;
  const message = String(err?.message || '').toLowerCase();
  const isTimeout = code === 'ECONNABORTED' || message.includes('timeout');
  const isNetwork = !err?.response || code === 'ERR_NETWORK' || message.includes('network error');
  const isServer = status >= 500 && status < 600;
  if (isTimeout || isNetwork || isServer) {
    return 'The catalog server is not responding yet. It may still be waking up after a deploy. Please wait a minute, then try Refresh catalog.';
  }
  return getErrorMessage(err, fallback);
}

/**
 * Fetch products with optional per-store catalog. Cache is keyed by storeId.
 * @param {string} apiBaseUrl
 * @param {{ storeId?: string, limit?: number, fields?: string }} [options]
 */
export function useProducts(apiBaseUrl, options = {}) {
  const storeId = options.storeId != null ? String(options.storeId).trim() : '';
  const limit = Number.isFinite(Number(options.limit)) && Number(options.limit) > 0
    ? Math.trunc(Number(options.limit))
    : 0;
  const fields = options.fields != null ? String(options.fields).trim() : '';
  const cacheScope = buildCacheScope({ storeId, limit, fields });

  const cachedOnMount = readCachedProducts(cacheScope);
  const initialFromCache = isFreshForFirstPaint(cachedOnMount);

  const [products, setProducts] = useState(() => (initialFromCache ? cachedOnMount.items : []));
  const [isLoading, setIsLoading] = useState(!initialFromCache);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSlow, setIsSlow] = useState(false);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(initialFromCache);
  const mounted = useRef(true);
  const productsRef = useRef(products);
  productsRef.current = products;
  const requestOptionsRef = useRef({ cacheScope, fields, limit, storeId });
  requestOptionsRef.current = { cacheScope, fields, limit, storeId };
  const lastRefreshAtRef = useRef(0);

  const load = useCallback(async ({ clearCache = false } = {}) => {
    const { cacheScope: scope, fields: requestedFields, limit: requestedLimit, storeId: sid } = requestOptionsRef.current;
    if (clearCache) {
      clearCachedProducts(scope);
    }
    const params = {
      ...(sid ? { storeId: sid } : {}),
      ...(requestedLimit ? { limit: requestedLimit } : {}),
      ...(requestedFields ? { fields: requestedFields } : {})
    };
    const hasProducts = productsRef.current.length > 0;
    setIsSlow(false);
    let slowTimer = null;
    if (!hasProducts) {
      slowTimer = window.setTimeout(() => {
        if (mounted.current) setIsSlow(true);
      }, SLOW_LOAD_NOTICE_MS);
    }
    if (hasProducts) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    lastRefreshAtRef.current = Date.now();

    try {
      const data = await fetchWithRetry(() => fetchProducts(params), {
        maxAttempts: 1,
        baseDelayMs: 600,
        maxDelayMs: 6000,
        backoffFactor: 2
      });
      if (!mounted.current) return;
      const list = Array.isArray(data) ? data : [];
      setProducts(list);
      setFromCache(false);
      writeCachedProducts(scope, list);
    } catch (err) {
      if (!mounted.current) return;
      const fallback = readCachedProducts(scope);
      if (fallback?.items?.length && isFreshForFallback(fallback)) {
        setProducts(fallback.items);
        setFromCache(true);
        setError(
          productLoadErrorMessage(
            err,
            'The catalog server is not responding yet. Showing saved catalog from your last visit.'
          )
        );
      } else {
        setError(
          productLoadErrorMessage(err, 'Failed to load products. Check your connection and try again.')
        );
      }
    } finally {
      if (slowTimer) window.clearTimeout(slowTimer);
      if (mounted.current) {
        setIsLoading(false);
        setIsRefreshing(false);
        setIsSlow(false);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    requestOptionsRef.current = { cacheScope, fields, limit, storeId };
    const cached = readCachedProducts(cacheScope);
    if (isFreshForFirstPaint(cached) && cached.items.length > 0) {
      setProducts(cached.items);
      setFromCache(true);
      setIsLoading(false);
      setIsRefreshing(true);
      load();
    } else {
      setProducts([]);
      setFromCache(false);
      setIsLoading(true);
      load();
    }
    return () => {
      mounted.current = false;
    };
  }, [apiBaseUrl, cacheScope, fields, limit, storeId, load]);

  const refetch = useCallback((options) => load(options), [load]);

  useEffect(() => {
    const onOnline = () => {
      if (error || fromCache) refetch();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [error, fromCache, refetch]);

  useEffect(() => {
    const onVisible = () => {
      const refreshIsStale = Date.now() - lastRefreshAtRef.current > FOCUS_REFRESH_MIN_AGE_MS;
      if (document.visibilityState === 'visible' && (refreshIsStale || error || fromCache)) {
        refetch();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [error, fromCache, refetch]);

  return { products, isLoading, isRefreshing, isSlow, error, refetch, fromCache };
}
